import { TableCell, TableRow } from '@/_components/ui/table'
import { cn } from '@/_lib/utils'
import type {
  TDeclarationMatchEnum,
  TIncomeDeltaDTO,
} from '@doutorsim/shared/exposed/person-declarations'
import type { RowConfig } from '../_helpers/field-config'
import { ConfirmedValueInput } from './confirmed-value-input'
import { OfficialValueCell } from './official-value-cell'
import { PatientValueCell } from './patient-value-cell'
import { StatusChip } from './status-chip'

interface DataConfirmationRowProps {
  row: RowConfig
  patientValue: string
  officialValue: string | null
  // Undefined when the row has no declaration yet (e.g. partial ViaCEP splits).
  match: TDeclarationMatchEnum | undefined
  incomeDelta: TIncomeDeltaDTO | null
  editedValue: string
  isChecked: boolean
  error?: string
  onValueChange: (value: string) => void
  onToggle: () => void
}

export const DataConfirmationRow = ({
  row,
  patientValue,
  officialValue,
  match,
  incomeDelta,
  editedValue,
  isChecked,
  error,
  onValueChange,
  onToggle,
}: DataConfirmationRowProps) => {
  const differs = match === 'mismatch'

  const rowState: 'checked' | 'differs' | 'pending' = isChecked
    ? 'checked'
    : differs
      ? 'differs'
      : 'pending'

  const leftStripe = {
    checked: 'border-l-brand-green-primary-5',
    differs: 'border-l-aux-red-danger-5',
    pending: 'border-l-brand-grayscale-5',
  }[rowState]

  const rowBg = {
    checked: 'bg-brand-green-primary-1/30',
    differs: 'bg-aux-red-danger-1/15',
    pending: 'bg-white hover:bg-brand-grayscale-1/60',
  }[rowState]

  return (
    <TableRow className={cn('border-b border-brand-grayscale-4 border-l-2', leftStripe, rowBg)}>
      <TableCell className="px-4 py-3 align-top">
        <span className="text-sm font-medium text-brand-grayscale-8">{row.label}</span>
      </TableCell>

      <OfficialValueCell row={row} officialValue={officialValue} />

      <PatientValueCell
        row={row}
        patientValue={patientValue}
        officialValue={officialValue}
        differs={differs}
        incomeDelta={incomeDelta}
      />

      <TableCell className="border-l border-brand-grayscale-4 px-4 py-3 align-top">
        <ConfirmedValueInput
          row={row}
          value={editedValue}
          onChange={onValueChange}
          disabled={isChecked}
          error={error}
        />
      </TableCell>

      <TableCell className="px-3 py-3 text-right align-top">
        <StatusChip state={rowState} onToggle={onToggle} />
      </TableCell>
    </TableRow>
  )
}
